import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Save, RotateCcw, Phone, PhoneOff, ThumbsUp, ThumbsDown, Settings, AlertCircle, XCircle, Mail, UserPlus, FileText, PhoneMissed } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';

const API = process.env.REACT_APP_BACKEND_URL;

const OUTCOMES = [
  { 
    key: 'interested', 
    label: 'Interested', 
    description: 'Lead showed interest during AI call',
    icon: ThumbsUp,
    color: 'bg-green-500'
  },
  { 
    key: 'not_interested', 
    label: 'Not Interested', 
    description: 'Lead declined or showed no interest',
    icon: ThumbsDown,
    color: 'bg-orange-500'
  },
  { 
    key: 'no_response', 
    label: 'No Response', 
    description: 'Call not connected (no answer, busy, etc.)',
    icon: PhoneOff,
    color: 'bg-gray-500'
  },
  { 
    key: 'failed_call', 
    label: 'Failed Call', 
    description: 'Call failed due to system error or API failure',
    icon: XCircle,
    color: 'bg-red-500'
  },
];

const TEAM_TYPES = [
  { value: 'telesales', label: 'Telesales Team' },
  { value: 'sales_agents', label: 'Sales Agents Team' },
];

const LEAD_STATUSES = [
  { value: 'pending_qualification', label: 'Pending Qualification' },
  { value: 'pre_qualified', label: 'Pre-Qualified' },
  { value: 'under_qualification', label: 'Under Qualification' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'new', label: 'New' },
];

const MISSING_MOBILE_ACTIONS = [
  { value: 'do_nothing', label: 'Do Nothing', icon: XCircle, description: 'Skip AI calling workflow' },
  { value: 'send_email', label: 'Send Email', icon: Mail, description: 'Send acknowledgment email if email exists' },
  { value: 'create_task', label: 'Create Task', icon: FileText, description: 'Create follow-up task for manual handling' },
  { value: 'assign_to_team', label: 'Assign to Team', icon: UserPlus, description: 'Directly assign to team without AI call' },
];

export default function AIAssignmentSettings() {
  const [activeTab, setActiveTab] = useState('outcomes');
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDefault, setIsDefault] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);
  
  // Missing Mobile Settings
  const [missingMobileSettings, setMissingMobileSettings] = useState({
    enabled: true,
    action_type: 'send_email',
    email_template_id: null,
    assignment_team: 'telesales',
    assignment_user_id: null,
    log_activity: true,
    create_task_title: 'Lead Missing Mobile - Follow Up Required',
    incomplete_status: 'incomplete_lead',
    both_missing_action: 'create_task',
    both_missing_task_title: 'Lead Missing Contact Info - Manual Follow Up Required'
  });
  const [missingMobileSaving, setMissingMobileSaving] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  
  const token = sessionStorage.getItem('auth_token');

  useEffect(() => {
    fetchSettings();
    fetchMissingMobileSettings();
    fetchSupportData();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/settings/ai-assignment?token=${token}`);
      if (response.data.success) {
        const settings = response.data.settings;
        setMappings(settings.mappings || []);
        setIsDefault(settings.is_default || false);
        setLastUpdated(settings.updated_at);
        setUpdatedBy(settings.updated_by_name || settings.updated_by);
      }
    } catch (error) {
      console.error('Failed to fetch AI assignment settings', error);
      toast.error('Failed to load settings');
      // Set defaults on error
      setMappings([
        { outcome: 'interested', team_type: 'telesales', lead_status: 'pending_qualification' },
        { outcome: 'not_interested', team_type: 'telesales', lead_status: 'pending_qualification' },
        { outcome: 'no_response', team_type: 'telesales', lead_status: 'pending_qualification' },
        { outcome: 'failed_call', team_type: 'telesales', lead_status: 'pending_qualification' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMissingMobileSettings = async () => {
    try {
      const response = await axios.get(`${API}/api/settings/ai-assignment/missing-mobile?token=${token}`);
      if (response.data.success) {
        setMissingMobileSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Failed to fetch missing mobile settings', error);
    }
  };

  const fetchSupportData = async () => {
    try {
      // Fetch email templates
      const templatesRes = await axios.get(`${API}/api/email-templates?token=${token}`);
      if (templatesRes.data) {
        setEmailTemplates(Array.isArray(templatesRes.data) ? templatesRes.data : templatesRes.data.templates || []);
      }
      
      // Fetch users for assignment
      const usersRes = await axios.get(`${API}/api/users?token=${token}`);
      if (usersRes.data) {
        setUsers(Array.isArray(usersRes.data) ? usersRes.data : usersRes.data.users || []);
      }
    } catch (error) {
      console.error('Failed to fetch support data', error);
    }
  };

  const handleMappingChange = (outcome, field, value) => {
    setMappings(prev => 
      prev.map(m => 
        m.outcome === outcome ? { ...m, [field]: value } : m
      )
    );
    setIsDefault(false);
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      const response = await axios.put(
        `${API}/api/settings/ai-assignment?token=${token}`,
        { mappings },
        { headers: { 'Content-Type': 'application/json' } }
      );
      
      if (response.data.success) {
        toast.success('Settings saved successfully');
        setIsDefault(false);
        setLastUpdated(response.data.settings.updated_at);
        setUpdatedBy(response.data.settings.updated_by);
      }
    } catch (error) {
      console.error('Failed to save settings', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    try {
      setSaving(true);
      const response = await axios.post(
        `${API}/api/settings/ai-assignment/reset?token=${token}`
      );
      
      if (response.data.success) {
        setMappings(response.data.settings.mappings);
        setIsDefault(true);
        setLastUpdated(null);
        setUpdatedBy(null);
        toast.success('Settings reset to defaults');
      }
    } catch (error) {
      console.error('Failed to reset settings', error);
      toast.error('Failed to reset settings');
    } finally {
      setSaving(false);
    }
  };

  const saveMissingMobileSettings = async () => {
    try {
      setMissingMobileSaving(true);
      const response = await axios.post(
        `${API}/api/settings/ai-assignment/missing-mobile?token=${token}`,
        missingMobileSettings
      );
      
      if (response.data.success) {
        toast.success('Missing mobile settings saved');
        setMissingMobileSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Failed to save missing mobile settings', error);
      toast.error('Failed to save settings');
    } finally {
      setMissingMobileSaving(false);
    }
  };

  const handleMissingMobileChange = (field, value) => {
    setMissingMobileSettings(prev => ({ ...prev, [field]: value }));
  };

  const getMappingForOutcome = (outcomeKey) => {
    return mappings.find(m => m.outcome === outcomeKey) || {
      outcome: outcomeKey,
      team_type: 'telesales',
      lead_status: 'under_qualification'
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="ai-assignment-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Settings className="h-6 w-6" />
            AI Calling Configuration
          </h1>
          <p className="text-slate-500 mt-1">
            Configure AI call outcomes and missing mobile number handling
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="outcomes" className="flex items-center gap-2">
            <Phone className="h-4 w-4" />
            Call Outcomes
          </TabsTrigger>
          <TabsTrigger value="missing-mobile" className="flex items-center gap-2">
            <PhoneMissed className="h-4 w-4" />
            Missing Mobile
          </TabsTrigger>
        </TabsList>

        {/* Call Outcomes Tab */}
        <TabsContent value="outcomes" className="space-y-4">
          <div className="flex justify-end gap-2">
            <Button 
              variant="outline" 
              onClick={resetToDefaults}
              disabled={saving || isDefault}
              data-testid="reset-defaults-btn"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset to Defaults
            </Button>
            <Button 
              onClick={saveSettings} 
              disabled={saving}
              data-testid="save-settings-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>

          {/* Status Banner */}
          {isDefault && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Using default settings. All outcomes set Lead Status to Pending Qualification.
              </AlertDescription>
            </Alert>
          )}

          {lastUpdated && !isDefault && (
            <div className="text-sm text-slate-500">
              Last updated: {new Date(lastUpdated).toLocaleString()} by {updatedBy}
            </div>
          )}

          {/* Mapping Cards */}
          <div className="space-y-4">
            {OUTCOMES.map((outcome) => {
              const mapping = getMappingForOutcome(outcome.key);
              const OutcomeIcon = outcome.icon;
              
              return (
                <Card key={outcome.key} data-testid={`outcome-card-${outcome.key}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${outcome.color}`}>
                        <OutcomeIcon className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{outcome.label}</CardTitle>
                        <CardDescription>{outcome.description}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Team Type */}
                      <div className="space-y-2">
                        <Label>Assign To Team</Label>
                        <Select
                          value={mapping.team_type}
                          onValueChange={(value) => handleMappingChange(outcome.key, 'team_type', value)}
                        >
                          <SelectTrigger data-testid={`team-select-${outcome.key}`}>
                            <SelectValue placeholder="Select team" />
                          </SelectTrigger>
                          <SelectContent>
                            {TEAM_TYPES.map((team) => (
                              <SelectItem key={team.value} value={team.value}>
                                {team.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Lead Status */}
                      <div className="space-y-2">
                        <Label>Set Lead Status</Label>
                        <Select
                          value={mapping.lead_status}
                          onValueChange={(value) => handleMappingChange(outcome.key, 'lead_status', value)}
                        >
                          <SelectTrigger data-testid={`status-select-${outcome.key}`}>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                          <SelectContent>
                            {LEAD_STATUSES.map((status) => (
                              <SelectItem key={status.value} value={status.value}>
                                {status.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Preview */}
                    <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                      <span className="text-sm text-slate-600">
                        When AI call outcome is <Badge className={outcome.color}>{outcome.label}</Badge>
                        {' → '}
                        Assign to <Badge variant="outline">{TEAM_TYPES.find(t => t.value === mapping.team_type)?.label}</Badge>
                        {' with status '}
                        <Badge variant="secondary">{LEAD_STATUSES.find(s => s.value === mapping.lead_status)?.label}</Badge>
                      </span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Missing Mobile Tab */}
        <TabsContent value="missing-mobile" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <PhoneMissed className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <CardTitle>Missing Mobile Number Handling</CardTitle>
                    <CardDescription>
                      Configure what happens when a lead is created without a mobile number
                    </CardDescription>
                  </div>
                </div>
                <Switch
                  checked={missingMobileSettings.enabled}
                  onCheckedChange={(checked) => handleMissingMobileChange('enabled', checked)}
                  data-testid="missing-mobile-enabled"
                />
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Primary Action */}
              <div className="space-y-3">
                <Label className="text-base font-medium">When Mobile is Missing but Email Exists</Label>
                <div className="grid grid-cols-2 gap-3">
                  {MISSING_MOBILE_ACTIONS.map((action) => {
                    const ActionIcon = action.icon;
                    const isSelected = missingMobileSettings.action_type === action.value;
                    return (
                      <div
                        key={action.value}
                        onClick={() => handleMissingMobileChange('action_type', action.value)}
                        className={`p-4 border rounded-lg cursor-pointer transition-all ${
                          isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid={`action-${action.value}`}
                      >
                        <div className="flex items-center gap-3">
                          <ActionIcon className={`h-5 w-5 ${isSelected ? 'text-blue-600' : 'text-gray-500'}`} />
                          <div>
                            <p className="font-medium">{action.label}</p>
                            <p className="text-xs text-gray-500">{action.description}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Email Template Selection */}
              {missingMobileSettings.action_type === 'send_email' && (
                <div className="space-y-2">
                  <Label>Email Template</Label>
                  <Select
                    value={missingMobileSettings.email_template_id || 'default'}
                    onValueChange={(value) => handleMissingMobileChange('email_template_id', value === 'default' ? null : value)}
                  >
                    <SelectTrigger data-testid="email-template-select">
                      <SelectValue placeholder="Select email template (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">Default Welcome Email</SelectItem>
                      {emailTemplates.map((template) => (
                        <SelectItem key={template.id} value={template.id}>
                          {template.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Task Title */}
              {missingMobileSettings.action_type === 'create_task' && (
                <div className="space-y-2">
                  <Label>Task Title</Label>
                  <Input
                    value={missingMobileSettings.create_task_title}
                    onChange={(e) => handleMissingMobileChange('create_task_title', e.target.value)}
                    placeholder="Enter task title"
                    data-testid="task-title-input"
                  />
                </div>
              )}

              {/* Team Assignment */}
              {missingMobileSettings.action_type === 'assign_to_team' && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Assign to Team</Label>
                    <Select
                      value={missingMobileSettings.assignment_team}
                      onValueChange={(value) => handleMissingMobileChange('assignment_team', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TEAM_TYPES.map((team) => (
                          <SelectItem key={team.value} value={team.value}>
                            {team.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Specific User (Optional)</Label>
                    <Select
                      value={missingMobileSettings.assignment_user_id || 'auto'}
                      onValueChange={(value) => handleMissingMobileChange('assignment_user_id', value === 'auto' ? null : value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Auto-assign" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">Auto-assign (Round Robin)</SelectItem>
                        {users.map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.full_name || user.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Both Missing Action */}
              <div className="pt-4 border-t space-y-3">
                <Label className="text-base font-medium">When Both Mobile AND Email are Missing</Label>
                <Select
                  value={missingMobileSettings.both_missing_action}
                  onValueChange={(value) => handleMissingMobileChange('both_missing_action', value)}
                >
                  <SelectTrigger data-testid="both-missing-action">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="create_task">Create Task for Manual Follow-up</SelectItem>
                    <SelectItem value="flag_incomplete">Flag as Incomplete Lead</SelectItem>
                    <SelectItem value="do_nothing">Do Nothing</SelectItem>
                  </SelectContent>
                </Select>
                
                {missingMobileSettings.both_missing_action === 'create_task' && (
                  <Input
                    value={missingMobileSettings.both_missing_task_title}
                    onChange={(e) => handleMissingMobileChange('both_missing_task_title', e.target.value)}
                    placeholder="Task title for missing contact info"
                    className="mt-2"
                  />
                )}
              </div>

              {/* Activity Logging */}
              <div className="flex items-center justify-between pt-4 border-t">
                <div>
                  <Label className="text-base">Log Activity</Label>
                  <p className="text-sm text-gray-500">Record all actions in the contact timeline</p>
                </div>
                <Switch
                  checked={missingMobileSettings.log_activity}
                  onCheckedChange={(checked) => handleMissingMobileChange('log_activity', checked)}
                />
              </div>

              {/* Save Button */}
              <div className="flex justify-end pt-4">
                <Button 
                  onClick={saveMissingMobileSettings} 
                  disabled={missingMobileSaving}
                  data-testid="save-missing-mobile-btn"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {missingMobileSaving ? 'Saving...' : 'Save Settings'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Info Section */}
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="pt-6">
              <h3 className="font-semibold mb-2 flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                When is this triggered?
              </h3>
              <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">
                <li>When a new lead is created without a mobile number</li>
                <li>When a lead is synced from HubSpot without phone</li>
                <li>When importing leads from CSV/Excel without mobile field</li>
              </ul>
              <p className="mt-3 text-sm text-slate-500">
                This prevents failed AI calls and ensures every lead gets proper follow-up.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
